import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NetworkStatus, USState } from '@strata/constants';

export interface PayerDetails {
    payerId: string;
    payerName: string;
    payerEmail?: string;
    payerFeatures?: string[];
}

export interface PrescriberDetails {
    prescriberNpi: string;
    name: string;
    firstName: string;
    telephoneNumber?: string;
    faxNumber?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: USState;
    postalCode?: string;
    organizationName?: string;
    practiceNpi?: string;
}

export interface SupplierDetails {
    name?: string;
    phone?: string;
    fax?: string;
    email?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: USState;
    zip?: string;
    npi: string;
    networkStatus: NetworkStatus;
    taxonomyCodes: { name: string; secondaryData: string }[];
    childNpis: string[];
    billingSupplierNpi?: string;
}

export interface ReferenceSliceState {
    payerDetails: Record<string, PayerDetails>;
    prescriberDetails: Record<string, PrescriberDetails>;
    supplierDetails: Record<string, SupplierDetails>;
}

const initialState: ReferenceSliceState = {
    payerDetails: {},
    prescriberDetails: {},
    supplierDetails: {},
};

export const referenceSlice = createSlice({
    name: 'reference',
    initialState,
    reducers: {
        setPayerDetails(state, action: PayloadAction<{ payerId: string; payer: PayerDetails }>) {
            const { payerId, payer } = action.payload;
            return {
                ...state,
                payerDetails: {
                    ...state.payerDetails,
                    [payerId]: payer,
                },
            };
        },
        setPrescriberDetails(
            state,
            action: PayloadAction<{ prescriberNpi: string; prescriber: PrescriberDetails }>,
        ) {
            const { prescriberNpi, prescriber } = action.payload;
            return {
                ...state,
                prescriberDetails: {
                    ...state.prescriberDetails,
                    [prescriberNpi]: prescriber,
                },
            };
        },
        setSupplierDetails(
            state,
            action: PayloadAction<{ supplierNpi: string; supplier: SupplierDetails }>,
        ) {
            const { supplierNpi, supplier } = action.payload;
            return {
                ...state,
                supplierDetails: {
                    ...state.supplierDetails,
                    [supplierNpi]: supplier,
                },
            };
        },
    },
});

export const { setPayerDetails, setPrescriberDetails, setSupplierDetails } = referenceSlice.actions;
